import { Check, ChefHat, UtensilsCrossed } from "lucide-react"
import { ScreenHeader } from "@/components/ui/headers"
import { cn } from "@/lib/utils"
import { DishIllustration } from "./dish-illustration"
import type { OrderTracking, TrackingStage } from "./types"
import { t } from "@/lib/i18n"
type TrackingScreenProps = {
    order: OrderTracking
}
const STAGE_ICON: Record<string, typeof ChefHat> = {
    received: Check,
    confirmed: Check,
    in_progress: ChefHat,
    ready: UtensilsCrossed,
}
export function TrackingScreen({ order }: TrackingScreenProps) {
    const eta = etaForOrder(order)
    return (<div className="text-foreground" data-testid="tracking-screen-view">
      <ScreenHeader title={`${t("ui.react.features.user.tracking_screen.order_mny")}${order.id.toString().padStart(4, "0")}`} subtitle={order.statusLabel}/>

      <div className="border-b bg-background px-4 py-4">
        <h1 className="text-xl font-semibold leading-tight tracking-tight">
          {headlineForOrder(order)}
        </h1>
        {eta !== null && (<div className="mt-2 flex items-baseline gap-2">
            <span className="text-4xl font-semibold tabular-nums tracking-tight text-brand">
              {eta}
            </span>
            <span className="text-xs leading-snug text-muted-foreground">{t("ui.react.features.user.tracking_screen.minutes")} {t("ui.react.features.user.tracking_screen.approx")}</span>
          </div>)}
      </div>

      <div className="px-4 py-5">
        <ol className="flex flex-col" data-testid="tracking-stages">
          {order.trackingStages.map((stage, index) => (<Stage key={stage.id} stage={stage} index={index} totalStages={order.trackingStages.length}/>))}
        </ol>
      </div>

      {/* Navigation back to the menu and the bill belongs to the tab bar, which
          is on screen already. What is not anywhere else is the dish itself: by
          the time an order is being tracked its menu entry is two screens away. */}
      <DishRecap order={order}/>
    </div>)
}
function DishRecap({ order }: {
    order: OrderTracking
}) {
    if (!order.itemImageUrl && !order.itemDescription)
        return null
    return (<div className="px-4 pb-6" data-testid="tracking-item-recap">
      <div className="overflow-hidden rounded-2xl border bg-card">
        {order.itemImageUrl && (<div className="relative aspect-[16/9] w-full">
            <DishIllustration src={order.itemImageUrl} alt={order.name}/>
          </div>)}
        <div className="space-y-1 p-4">
          <p className="text-sm font-semibold leading-tight tracking-tight">{order.name}</p>
          {order.itemDescription && (<p className="text-sm leading-snug text-muted-foreground" data-testid="tracking-item-description">
              {order.itemDescription}
            </p>)}
        </div>
      </div>
    </div>)
}
function Stage({ stage, index, totalStages, }: {
    stage: TrackingStage
    index: number
    totalStages: number
}) {
    const Icon = stage.state === "done" ? Check : STAGE_ICON[stage.id] ?? Check
    const isLast = index === totalStages - 1
    return (<li data-testid={`tracking-stage-${stage.id}`} data-state={stage.state} className="relative flex gap-3.5 py-2.5">
      {!isLast && (<span className={cn("absolute bottom-[-10px] left-[13px] top-[30px] w-0.5", stage.state === "done" ? "bg-status-available/50" : "bg-border")} aria-hidden/>)}
      <span className={cn("relative z-[1] flex size-7 shrink-0 items-center justify-center rounded-full border-2 bg-card", stage.state === "done"
            ? "border-status-available bg-status-available/10 text-status-available"
            : stage.state === "active"
                ? "animate-pulse border-brand text-brand"
                : stage.state === "cancelled"
                    ? "border-destructive text-destructive"
                    : "border-border text-muted-foreground")}>
        <Icon className="size-3.5"/>
      </span>
      <div className="flex-1">
        <p className="text-sm font-medium tracking-tight">{stage.label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t(`ui.user.tracking.${stage.state}`)}
        </p>
      </div>
    </li>)
}
function headlineForOrder(order: OrderTracking) {
    if (order.status === "ready" || order.status === "completed" || order.status === "ready_for_pickup") {
        return t("ui.user.tracking.ready_status", { name: order.name })
    }
    if (order.status === "cancelled")
        return t("ui.user.tracking.cancelled_status", { name: order.name })
    return t("ui.user.tracking.preparing_status", { name: order.name })
}
function etaForOrder(order: OrderTracking) {
    if (order.pickupEtaMinutes && order.pickupEtaMinutes > 0)
        return order.pickupEtaMinutes
    return null
}
